<template>
  <div class="admin-edit-course">
    <router-link to="/admin/courses" class="btn-flat black-text mt-2">
      <i class="material-icons left">arrow_back</i>
      {{ isEng ? 'Go back' : 'Tillbaka' }}
    </router-link>
    <h1>
      <span v-if="course">
        {{ this.$store.state.user.langEng
          ? `Course for ${course.klassSlug.toUpperCase()}`
          : `Kurs för ${course.klassSlug.toUpperCase()}`
        }}
      </span>
      <span v-else>
        {{ this.$store.state.user.langEng
          ? `Create new course`
          : `Skapa ny kurs`
        }}
      </span>
    </h1>
    <div class="edit-wrapper">
      <form>
        <!-- Input Couse Name -->
        <div class="row form-row">
          <div class="name-and-points-headers">
            <h4>{{ $store.state.user.langEng ? 'Name' : 'Namn' }}</h4>
            <h4 class="header-points">{{ $store.state.user.langEng ? 'Points' : 'Poäng' }}</h4>
          </div>
          <div class="name-and-points-inputs">
            <div class="admin-input-field">
              <input
                type="text"
                v-model="course.name"
                :placeholder="$store.state.user.langEng ? 'Course name' : 'Kurs namn'"
              />
            </div>
            <div class="admin-input-field period">
              <input
                type="text"
                v-model="course.points"
                :placeholder="$store.state.user.langEng ? 'Course points' : 'Kurs poäng'"
              />
            </div>
          </div>
        </div>
        <!-- Input Course Slug -->
        <div class="row form-row">
          <h4>Slug</h4>
          <div class="admin-input-field"
            :class="{'error-outline': slugError}">
            <input
              type="text"
              v-model="slug"
              :placeholder="$store.state.user.langEng ? 'Course slug' : 'Kurs slug'"
            />
          </div>
        </div>
        <!-- Input Course Syllabus -->
        <div class="row form-row">
          <h4>{{ $store.state.user.langEng ? 'Syllabus' : 'Kursplan' }}</h4>
          <div class="admin-input-field input-icons">
            <input
              type="text"
              v-model="course.syllabus"
              :placeholder="$store.state.user.langEng ? 'Course syllabus' : 'Kurs kursplan'"
            />
            <a target="_blank" :href="course.syllabus"><i v-show="course.syllabus" class="material-icons">pageview</i></a>
          </div>
        </div>
        <!-- Select Course Head-teacher -->
        <div class="row form-row">
          <h4>{{ $store.state.user.langEng ? 'Head-teacher' : 'Kursansvarig lärare' }}</h4>
          <admin-select
            v-if="create || course.courseResponsible"
            class="admin-select"
            :data="personnel"
            :initial="course.courseResponsible"
            v-model="course.courseResponsible"
          />
        </div>
        <!-- Select Course Assisting-teachers -->
        <div class="row form-row">
          <h4>{{ $store.state.user.langEng ? 'Assisting teachers' : 'Ytterligare lärare' }}</h4>
          <admin-select
            v-if="create || course.teachers.length"
            class="admin-select"
            :data="personnel"
            :initial="course.teachers"
            :multiple="true"
            :disabled="course.courseResponsible"
            v-model="course.teachers"
          />
        </div>
        <!-- Input Course Start Date -->
        <div class="row form-row">
          <h4>
            {{ $store.state.user.langEng ? 'Course start' : 'Kurs start' }}
          </h4>
          <div class="admin-input-field period">
            <!-- <span>{{ getWeek(course.start) }}</span> -->
            <input :placeholder="$store.state.user.langEng ? 'Start date' : 'Startdatum'" 
              type="text" 
              :value="`(v${startWeek}) ${course.start.toISOString().slice(0, 10)}`"
              class="datepicker course-start">
          </div> 
        </div>
        <!-- Input Course End Date -->
        <div class="row form-row">
          <h4>
            {{ $store.state.user.langEng ? 'Course end' : 'Kurs slut' }}
          </h4>
          <div class="admin-input-field period">
            <input
              type="text"
              :value="`(v${endWeek}) ${course.end.toISOString().slice(0, 10)}`"
              class="datepicker course-end"
              :placeholder="$store.state.user.langEng ? 'End date' : 'Slutdatum'"
            />
          </div>
        </div>
      </form>
      <!-- side edit panel -->
      <div class="edit-side-panel">
        <div class="form-row">
          <h4 class="pb-2">{{ $store.state.user.langEng ? 'Upload syllabus' : 'Ladda upp kursplan' }}</h4>
          <file-upload id="file-upload"  />
        </div>
        <div class="form-row pt-5">
          <router-link to="/admin/courses" class="btn-flat mb-2 text-center" id="newCourseButton">
            {{ $store.state.user.langEng ? 'Cancel' : 'Avbryt' }}
          </router-link>
          <a @click="saveCourse" class="btn-flat mb-2 text-center" id="newCourseButton">
            {{ $store.state.user.langEng ? 'Save' : 'Spara' }}
          </a>
          <a v-if="isMobile" @click="deactivateCourse" class="btn grey white-text">
            {{ 
              course.inactive ? 
              $store.state.user.langEng ? 'Activate' : 'Aktivera' :
              $store.state.user.langEng ? 'Deactivate' : 'Inaktivera' 
            }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Vue, Component, Watch } from 'vue-property-decorator';
import AdminSelect from '@/components/Admin/AdminSelect.vue';
import FileUpload from '@/components/CreateArticle/FileUpload/FileUpload.vue';
import { Api, Rest, getWeek, string_to_slug } from '../utils.js';
import { eventBus } from "@/main";

@Component({
  components: { AdminSelect, FileUpload }
})
export default class AdminEditCourse extends Vue {
  course = {
    klassSlug: '',
    name: '',
    points: 0,
    inactive: false,
    slug: '',
    syllabus: '',
    courseResponsible: '',
    start: new Date(),
    end: new Date(),
    teachers: [],
  }; // course object
  create = false; // if we're creating a new course
  slugError = false

  get slug() {
    return this.course.slug
  }

  set slug(val) {
    this.course.slug = string_to_slug(val)
  }

  get startWeek() {    
    return getWeek(this.course.start)
  }

  get endWeek() {
    return getWeek(this.course.end)
  }

  get isMobile() {
    return this.$store.state.isMobile;
  }

  get isEng() {
    return this.$store.state.user.langEng;
  }

  get personnel() {
    this.initSelectors()
    
    return this.$store.state.personnel.map(p => ({
      value: p.id,
      text: `${p.firstname} ${p.lastname}`
    }))
  }

  @Watch('course.courseResponsible')
  updateCourseTeachers() {
    if(!this.course.teachers.includes(this.course.courseResponsible)) {
      this.course.teachers.push(this.course.courseResponsible)
    }
    
    this.initSelectors()
  }

  @Watch('course.slug')
  validateSlug(slug) {
    clearTimeout(this.throttleSlug)
    this.throttleSlug = setTimeout(async () => {
      const slugTaken = await Rest.get('Course', { properties: { slug } })
      this.slugError = (slugTaken.length && slugTaken[0].id != this.course.id) || false
    }, 200);
  }

  @Watch('course.points')
  validatePoints() {
    this.validatePeriod()
  }

  validatePeriod() {
    const didValidate = (+this.course.points / 5) === (this.endWeek - this.startWeek)
    if(didValidate) {
      document.querySelectorAll('.period').forEach(el => {
        el.classList.remove('error-outline')
      })
    } else {
      document.querySelectorAll('.period').forEach(el => {
        el.classList.add('error-outline')
      })
    }

    return didValidate
  }

  async saveCourse() {
    if(this.slugError) return 

    let courseToSave = {...this.course}
    courseToSave.points = +courseToSave.points
    courseToSave.start = courseToSave.start.getTime()
    courseToSave.end = courseToSave.end.getTime()
    !courseToSave.syllabus && (courseToSave.syllabus = ' ')


    let res, text;
    if(this.course._id) {
      res = await Rest.put('Course', courseToSave)
      text = this.$store.state.user.langEng
        ? "Saved course"
        : "Sparade kurs"
    } else {
      res = await Rest.post('Course', courseToSave)
      this.course._id = res._id
      this.course.id = res.id

      history.replaceState(null, null, `/admin/courses/${this.course.slug}/edit`)

      text = this.$store.state.user.langEng
        ? "Created new course"
        : "Skapade ny kurs"
    }
    if(res._id) {
      this.$store.commit("toggleSnackbar", { text })
    }
  }

  async deactivateCourse() {
    let courseToUpdate = {
      id: this.course.id,
      inactive: !this.course.inactive
    }

    let res = await Api.post('admin/set-active/Course', courseToUpdate)
    if(res.id) {
      this.course.inactive = res.inactive
    }
  }

  async fetchKlassAndCourse() {
    const { slug } = this.$route.params; // course slug
    const resCourse = await Rest.get('Course', {
      properties: { slug },
      eager: { teachers: true },
    });

    if (!resCourse[0].id) {
      console.error('Invalid course slug');
      return; 
    }

    resCourse[0].start = new Date(resCourse[0].start)
    resCourse[0].end = new Date(resCourse[0].end)
    resCourse[0].syllabus = resCourse[0].syllabus.trim()
    Object.assign(this.course, resCourse[0])

    let headTeacher = this.course.teachers.filter(t => t.courseResponsible)[0]
    this.course.teachers = this.course.teachers.map(t => t.id)

    if(headTeacher) {
      this.course.courseResponsible = headTeacher.id
    } else {
      this.create = true
    }
  }

  initSelectors() {
    setTimeout(() => {
      const elems = document.querySelectorAll('select');
      this.$M.FormSelect.init(elems);
    }, 0);
  }

  mounted() {
    let modalOptions = {
      autoClose: true,
      format: 'yyyy-mm-dd',
      onClose: () => {
        document.querySelector('#header-content').style.zIndex = 5
      }
    }

    let start = document.querySelectorAll('.course-start');
    this.$M.Datepicker.init(start, { 
      onSelect: d => {
        d.setHours(d.getHours() + 2)
        this.course.start = d
        this.validatePeriod()
      }, 
      onOpen: () => {
        document.querySelector('#header-content').style.zIndex = 1
        let date = document.querySelector(".course-start");
        let instance = this.$M.Datepicker.getInstance(date)
        setTimeout(() => {
          instance.gotoDate(new Date(this.course.start));
          instance.setDate(new Date(this.course.start));
        }, 0)
      },
      ...modalOptions});
    let end = document.querySelectorAll('.course-end');
    this.$M.Datepicker.init(end, { 
      onSelect: d => {
        d.setHours(d.getHours() + 2)
        this.course.end = d
        this.validatePeriod()
      },
      onOpen: () => {
        document.querySelector('#header-content').style.zIndex = 1
        let date = document.querySelector(".course-end");
        let instance = this.$M.Datepicker.getInstance(date)
        setTimeout(() => {
          instance.gotoDate(new Date(this.course.end));
          instance.setDate(new Date(this.course.end));
        }, 0)
      },
      ...modalOptions});
  }

  async created() {
    // check if we're creating a new course (aka. no slug provided)
    if (this.$route.params.slug) {
      await this.fetchKlassAndCourse();
    } else {
      this.create = true;
      this.course.klassSlug = this.$store.state.admin.selectedKlass || 
                              this.$store.getters.classNameInUrl
    }

    // init materialize
    this.initSelectors()

    // set course syllabus to newly uploaded file url
    eventBus.$on("addFileToContentArea", (data) => {
      this.course.syllabus = data.id
    })
  }

  beforeDestroy() {
    eventBus.$off("addFileToContentArea");
  }
}
</script>

<style scoped lang="scss">
.name-and-points-headers {
  display: grid;
  grid-template-columns: 1fr 1fr;
  .header-points {
    text-align: end;
  }
}

.name-and-points-inputs {
  display: grid;
  grid-template-columns: 1fr 50px;
  column-gap: 10px;
}

.admin-edit-course {
  .edit-wrapper {
    display: grid;
    grid-template-columns: 1fr 0.4fr;
    // column-gap: 1em;

    h4 {
      font-size: 1.2em;
      font-weight: 500;
      padding-bottom: 5px;   
    }

    #file-upload {
      background-color: red;
    }
    .btn-flat {
      border: solid 1px lightgrey;
      color: #595a5e;
      font-weight: bold;
      text-transform: initial;

      &:hover {
        background-color: lightgray;
      }
    }
  }

  h1 {
    font-size: 1.5em;
    font-weight: bold;
    margin-left: 0.8em;
    padding: 0.5em 0 1.5em 0;
  }
  
  .form-row {
    display: grid;
    width: 95%;
    padding-left: 2em;
  }

  .admin-input-field {
    border: 1px solid lightgray;
    border-radius: 4px;
    color: #595a5e;
    padding: 0.2em 0.5em;

    &.input-icons {
      display: grid;
      grid-template-columns: 1fr auto;
      a {
        align-self: center;
        line-height: 1em;
        color: var(--main-text-color);
        &:hover {
          opacity: 60%;
        }
      }
    }
  }
  
  .admin-select {
    border-radius: 4px;
    border: solid 1px lightgrey;
    padding: 0.2em 0 !important;
    color: #595a5e;
  }
}

.mobile {
  .edit-wrapper {
    grid-template: 1fr / 1fr;
  }

  .edit-side-panel .form-row {
    margin: 0;
    padding-left: 1.2em;
    width: 92%;
  }
}
</style>
